import { DateTime } from 'ts-luxon';

import { mapDeviceAuthenticationFromSysApi } from '@shure/shared/angular/data-access/device-authentication';
import { DeviceNetworkTimeProtocolServerConfigurationMode } from '@shure/shared/angular/data-access/system-api/core';
import { DeviceNtpNetworkProtocolMode } from '@shure/shared/angular/data-access/system-api/models';

import { NtpDateTimeSettingsDevice } from '../../api';
import { NtpDateTimeSettingsDeviceFragment } from '../graphql/generated/ntp-date-time.generated';

export function mapNtpDateTimeSettingsFromSysApi(
	deviceSysApi: NtpDateTimeSettingsDeviceFragment,
	authenticated: boolean | false,
	readonlyModeSelected: boolean | false
): NtpDateTimeSettingsDevice {
	const {
		id,
		description: { features },
		features: { networkTimeProtocolServerConfiguration, networkTimeProtocol, authentication }
	} = deviceSysApi;

	return {
		id,
		features: {
			networkTimeProtocol: features.networkTimeProtocol.supported
				? {
						currentTime: formatNtpDateTime(networkTimeProtocol?.currentTime),
						enabled: networkTimeProtocol?.enabled,
						lastSynchronizedTime: formatNtpDateTime(networkTimeProtocol?.lastSynchronizedTime),
						synchronized: networkTimeProtocol?.synchronized,
						isMissing: !networkTimeProtocol
					}
				: undefined,
			networkTimeProtocolServerConfiguration: features.networkTimeProtocolServerConfiguration.supported
				? {
						automaticConfiguration: networkTimeProtocolServerConfiguration?.automaticConfiguration ?? [],
						manualConfiguration: networkTimeProtocolServerConfiguration?.manualConfiguration ?? [],
						mode: mapNetworkTimeProtocolModeFromSysApi(networkTimeProtocolServerConfiguration?.mode),
						isMissing: !networkTimeProtocolServerConfiguration
					}
				: undefined,
			authentication: mapDeviceAuthenticationFromSysApi(authentication, authenticated, readonlyModeSelected)
		}
	};
}

export function mapNetworkTimeProtocolModeFromSysApi(
	mode: DeviceNetworkTimeProtocolServerConfigurationMode | null | undefined
): DeviceNtpNetworkProtocolMode | undefined {
	switch (mode) {
		case DeviceNetworkTimeProtocolServerConfigurationMode.Automatic:
			return DeviceNtpNetworkProtocolMode.Automatic;
		case DeviceNetworkTimeProtocolServerConfigurationMode.Manual:
			return DeviceNtpNetworkProtocolMode.Manual;
		default:
			return undefined;
	}
}

/** Formats an ISO 8601 datetime string as `HH:mm:ss; yyyy-MMM-dd, UTC`. */
export function formatNtpDateTime(isoString: string | null | undefined): string | undefined {
	if (!isoString) {
		return undefined;
	}
	const dt = DateTime.fromISO(isoString).toUTC();
	return dt.isValid ? dt.toFormat("HH:mm:ss; yyyy-MMM-dd, 'UTC'") : undefined;
}
